import React, {Dispatch, FC, SetStateAction, useEffect, useState} from 'react';
import {Col, Form, message, Radio, Row,} from "antd";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {clearCreateEmptyLeg, createEmptyLeg} from "../../../../../store/reducers/emptyLegsReducer/emptyLegsReducer";
import {CustomDatePicker} from "../../../../../components/UI/AntCustom/CustomDatePicker";
import {DateAndTimePicker} from "../../../../../components/UI/AntCustom/DateAndTimePicker";
import {DateAndTimeRangePicker} from "../../../../../components/UI/AntCustom/DateAndTimeRangePicker";
import {Dayjs} from "dayjs";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {useAirportFilter} from "../../../../../utils/hooks/useAirportFilter";
import {fetchCurrencies} from "../../../../../utils/helpers/selectDataFetchers/fetchCurrencies";
import {fetchOwnPlanes} from "../../../../../utils/helpers/selectDataFetchers/fetchOwnPlanes";
import {EmptyLegsTabKeysEnum} from "../EmptyLegsTabKeysEnum";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {RenderConfirmModal, renderSuccessModal,} from "../../../../../components/UI/StatusModals/renderSuccessModal";
import {isDateInPast} from "../../../../../utils/helpers/dateHelpers/scheduleDateHelpers";
import {EmptyLegDateTypeEnum, useEmptyLegDateTypeName} from "../../../../../utils/constans/business/emptyLegDateType";
import {dayjsToUTCDate} from "../../../../../utils/helpers/dateHelpers/dateHelpers";
import {CalculateFlight} from "../../../../../utils/helpers/formHelpers/CalculateFlight";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CommonUnitMessages} from "../../../../../intl/commonMessages/CommonUnitMessages";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {CalculateSwitcher} from "../../../../../components/businessCommon/calculateButton/CalculateSwitcher";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import {CustomSwitch} from "../../../../../components/UI/AntCustom/CustomSwitch";
import {useIsMobile} from "../../../../../hooks/useWindowSize";
import {convertFromKm, convertToKm, getUnitLabel} from "../../../../../utils/helpers/distanceConverter/distanceConverter";
import {DistanceUnit} from "../../../../../utils/constans/statuses/distanceUnits";
import {getDateAndTimePickerValidationRules} from "../../../../../utils/helpers/formHelpers/getDateAndTimePickerValidationRules";
import {API} from "../../../../../API/API";
import {AirportApiGetAirportRequest} from "../../../../../proto/generated/api_entities_pb";
import {mapAirportEntityToIAirport} from "../../../../../store/reducers/fleetReducer/fleetMappers";
import {LocalTimeDisplay} from "../../../../../components/businessCommon/localTimeDisplay/LocalTimeDisplay";
import styled from "styled-components";


const LocalTimeWrapper = styled.div`
  margin-top: 0px;
  display: flex;
  justify-content: flex-end;
  min-height: 18px;
`;

const AddEmptyLegModalMessages = defineMessages({
  title: {
    id: "addEmptyLegModal.title",
    defaultMessage: "Create Empty Leg",
  },
  createWarn: {
    id: "addEmptyLegModal.createWarn",
    defaultMessage: "Flight duration and flying range were filled out manually. Do you want to create an Empty Leg?",
  },
  success: {
    id: "addEmptyLegModal.success",
    defaultMessage: "New Empty Leg has been successfully created",
  }
})

interface ICreateEmptyLegFormData {
  dateTypeRadio: EmptyLegDateTypeEnum;
  availableDates?: [Dayjs, Dayjs];
  departureDate?: Dayjs;
  airportFromId: number;
  airportToId: number;
  fleetId: number;
  priceAmount: number;
  priceCurrencyId: number;
  hours: number;
  minutes: number;
  distance: number;
  comment: string;
}

interface IAddEmptyLegModal {
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  setActiveTabKey: Dispatch<SetStateAction<EmptyLegsTabKeysEnum>>;
}

export const AddEmptyLegModal: FC<IAddEmptyLegModal> = ({isModalVisible, setIsModalVisible, setActiveTabKey}) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const {
    isLoading,
    isSuccess,
    errorMessage,
  } = useAppSelector(state => state.emptyLegs.create);
  const userDistanceUnit = useAppSelector(state => state.user.oneItemObject.item.distanceUnit) || DistanceUnit.NAUTICAL_MILE;
  const [dateTypeRadioValue, setDateTypeRadioValue] = useState<EmptyLegDateTypeEnum>(EmptyLegDateTypeEnum.RANGE);
  const [isAutoFlyRouteDisabled, setIsAutoFlyRouteDisabled] = useState<boolean>(true);
  const [isAutoFlyRouteCalcApplied, setIsAutoFlyRouteApplied] = useState<boolean>(false);
  const [isPriceOnRequest, setIsPriceOnRequest] = useState<boolean>(false);
  const [airportFromOffset, setAirportFromOffset] = useState<number | null>(null);
  const [airportToOffset, setAirportToOffset] = useState<number | null>(null);
  const emptyLegDateTypeNameRange = useEmptyLegDateTypeName(EmptyLegDateTypeEnum.RANGE);
  const emptyLegDateTypeNameSpecific = useEmptyLegDateTypeName(EmptyLegDateTypeEnum.SPECIFIC);
  
  // Отслеживаем значения для отображения local time
  const departureDate = Form.useWatch('departureDate', form);
  const airportFromId = Form.useWatch('airportFromId', form);
  
  // Хуки для фильтрации аэропортов
  const fromAirportFilter = useAirportFilter(form, 'airportToId');
  const toAirportFilter = useAirportFilter(form, 'airportFromId');

  useEffect(() => {
    return () => {
      dispatch(clearCreateEmptyLeg());
    }
  }, [])


  useEffect(() => {
    if (!isLoading) {
      if (isSuccess) {
        renderSuccessModal(intl.formatMessage(AddEmptyLegModalMessages.success), () => {
          setActiveTabKey(EmptyLegsTabKeysEnum.ACTIVE);
          setIsModalVisible(false);
        });
      }
      if (errorMessage)
        message.error(errorMessage);
    }
  }, [isSuccess, errorMessage, isLoading])

  // Загрузка информации об аэропортах для получения timezone offset
  useEffect(() => {
    const loadAirportInfo = async () => {
      const airportFromId = form.getFieldValue('airportFromId');
      const airportToId = form.getFieldValue('airportToId');

      if (airportFromId) {
        try {
          const req = new AirportApiGetAirportRequest().setAirportid(airportFromId);
          const airportEntity = (await API.getAirportById(req)).getAirport();
          if (airportEntity) {
            const airport = mapAirportEntityToIAirport(airportEntity);
            setAirportFromOffset(airport.timezone?.offset ?? null);
          } else {
            setAirportFromOffset(null);
          }
        } catch (error) {
          console.error('Failed to load airport from:', error);
          setAirportFromOffset(null);
        }
      } else {
        setAirportFromOffset(null);
      }

      if (airportToId) {
        try {
          const req = new AirportApiGetAirportRequest().setAirportid(airportToId);
          const airportEntity = (await API.getAirportById(req)).getAirport();
          if (airportEntity) {
            const airport = mapAirportEntityToIAirport(airportEntity);
            setAirportToOffset(airport.timezone?.offset ?? null);
          } else {
            setAirportToOffset(null);
          }
        } catch (error) {
          console.error('Failed to load airport to:', error);
          setAirportToOffset(null);
        }
      } else {
        setAirportToOffset(null);
      }
    };

    if (isModalVisible) {
      loadAirportInfo();
    }
  }, [form, isModalVisible])

  const onFinish = (values: ICreateEmptyLegFormData) => {
    if (values.minutes === 0 && values.hours === 0) {
      message.error(intl.formatMessage(CommonFormMessages.ruleIncorrectFlightTime));
      return;
    }

    const createEmptyLegCallback = () => dispatch(createEmptyLeg({
      availableFromDate: values.availableDates ? dayjsToUTCDate(values.availableDates[0]) : undefined,
      availableToDate: values.availableDates ? dayjsToUTCDate(values.availableDates[1]) : undefined,
      departureDate: values.departureDate ? dayjsToUTCDate(values.departureDate) : undefined,
      airportFromId: values.airportFromId,
      airportToId: values.airportToId,
      fleetId: values.fleetId,
      priceAmount: isPriceOnRequest ? 0 : values.priceAmount,
      priceCurrencyId: isPriceOnRequest ? 0 : values.priceCurrencyId,
      hours: values.hours,
      minutes: values.minutes,
      distance: convertToKm(values.distance, userDistanceUnit),
      comment: values.comment,
      priceOnRequest: isPriceOnRequest,
    }))

    if (!isAutoFlyRouteCalcApplied)
      RenderConfirmModal(
        intl.formatMessage(AddEmptyLegModalMessages.createWarn),
        createEmptyLegCallback,
        intl);
    else
      createEmptyLegCallback();
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const handleFormValuesChange = async (changedValues: any) => {
    const fieldName = Object.keys(changedValues)[0];

    if (fieldName === "dateTypeRadio" && changedValues.hasOwnProperty(fieldName)) {
      const value = changedValues[fieldName];
      setDateTypeRadioValue(value)
    } else if (["hours", "minutes", "distance"].includes(fieldName)) {
      setIsAutoFlyRouteApplied(false);

      const planeId: number | undefined = form.getFieldValue('fleetId');
      const airportFromId: number | undefined = form.getFieldValue('airportFromId');
      const airportToId: number | undefined = form.getFieldValue('airportToId');
      if (planeId && airportFromId && airportToId)
        setIsAutoFlyRouteDisabled(false);
      else
        setIsAutoFlyRouteDisabled(true);
    } else if (["fleetId", "airportFromId", "airportToId"].includes(fieldName)) {
      await onCalculateFlight();
      
      // Загружаем информацию об аэропортах при изменении
      const airportFromId = fieldName === 'airportFromId' ? changedValues[fieldName] : form.getFieldValue('airportFromId');
      const airportToId = fieldName === 'airportToId' ? changedValues[fieldName] : form.getFieldValue('airportToId');

      if (airportFromId) {
        try {
          const req = new AirportApiGetAirportRequest().setAirportid(airportFromId);
          const airportEntity = (await API.getAirportById(req)).getAirport();
          if (airportEntity) {
            const airport = mapAirportEntityToIAirport(airportEntity);
            setAirportFromOffset(airport.timezone?.offset ?? null);
          } else {
            setAirportFromOffset(null);
          }
        } catch (error) {
          console.error('Failed to load airport from:', error);
          setAirportFromOffset(null);
        }
      } else {
        setAirportFromOffset(null);
      }

      if (airportToId) {
        try {
          const req = new AirportApiGetAirportRequest().setAirportid(airportToId);
          const airportEntity = (await API.getAirportById(req)).getAirport();
          if (airportEntity) {
            const airport = mapAirportEntityToIAirport(airportEntity);
            setAirportToOffset(airport.timezone?.offset ?? null);
          } else {
            setAirportToOffset(null);
          }
        } catch (error) {
          console.error('Failed to load airport to:', error);
          setAirportToOffset(null);
        }
      } else {
        setAirportToOffset(null);
      }
    }
  }

  const onCalculateFlight = async () => {
    const planeId: number | undefined = form.getFieldValue('fleetId');
    const airportFromId: number | undefined = form.getFieldValue('airportFromId');
    const airportToId: number | undefined = form.getFieldValue('airportToId');

    if (planeId && airportFromId && airportToId) {
      if (isAutoFlyRouteCalcApplied) {
        setIsAutoFlyRouteApplied(false);
      } else {
        const {
          hours,
          minutes,
          distance,
          isAutoCalculationApplied,
        } = await CalculateFlight(airportFromId, airportToId, planeId, intl)

        form.setFieldsValue({
          'hours': hours,
          'minutes': minutes,
          'distance': convertFromKm(distance, userDistanceUnit),
        });
        setIsAutoFlyRouteApplied(isAutoCalculationApplied);
      }
    } else {
      setIsAutoFlyRouteDisabled(true);
      setIsAutoFlyRouteApplied(false);
    }
  }

  const initialValues: Partial<ICreateEmptyLegFormData> = {
    dateTypeRadio: EmptyLegDateTypeEnum.RANGE,
    availableDates: undefined,
    departureDate: undefined,
    airportFromId: undefined,
    airportToId: undefined,
    fleetId: undefined,
    priceAmount: undefined,
    priceCurrencyId: undefined,
    hours: undefined,
    minutes: undefined,
    distance: undefined,
    comment: undefined,
  }

  return (
    <CustomModal
      title={intl.formatMessage(AddEmptyLegModalMessages.title)}
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      centered
      footer={null}
    >
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout={'vertical'}
        initialValues={initialValues}
        onValuesChange={handleFormValuesChange}
      >
        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.planeLabel)}
            name={'fleetId'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <DebounceSelect
              fetchOptions={fetchOwnPlanes}
              placeholder={intl.formatMessage(CommonFormMessages.planePlaceholder)}
            />
          </CustomFieldInput>
        </Col>

        <Col flex={1}>
          <Row gutter={16} wrap={false}>
            <Col flex="1" style={{minWidth: 0}}>
              <CustomFieldInput
                label={intl.formatMessage(CommonFormMessages.fromAirportLabel)}
                name={'airportFromId'}
                rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
              >
                <DebounceSelect
                  key={fromAirportFilter.key}
                  fetchOptions={fromAirportFilter.fetchOptions}
                  placeholder={intl.formatMessage(CommonFormMessages.fromAirportPlaceholder)}
                  showSearch={true}
                />
              </CustomFieldInput>
            </Col>

            <Col flex="0 0 auto" style={{fontSize: '14px', color: '#666', fontWeight: 500, padding: '0 4px', minWidth: '20px', marginTop: '38px'}}>
              —
            </Col>

            <Col flex="1" style={{minWidth: 0}}>
              <CustomFieldInput
                label={intl.formatMessage(CommonFormMessages.toAirportLabel)}
                name={'airportToId'}
                rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
              >
                <DebounceSelect
                  key={toAirportFilter.key}
                  fetchOptions={toAirportFilter.fetchOptions}
                  placeholder={intl.formatMessage(CommonFormMessages.toAirportPlaceholder)}
                  showSearch={true}
                />
              </CustomFieldInput>
            </Col>
          </Row>
        </Col>
        <Col flex={1}>
          <CustomFieldInput
            name="dateTypeRadio"
            style={{marginBottom: 8}}
          >
            <Radio.Group>
              <Radio value={EmptyLegDateTypeEnum.RANGE}>{emptyLegDateTypeNameRange}</Radio>
              <Radio
                value={EmptyLegDateTypeEnum.SPECIFIC}>{emptyLegDateTypeNameSpecific}</Radio>
            </Radio.Group>
          </CustomFieldInput>

          {dateTypeRadioValue === EmptyLegDateTypeEnum.RANGE &&
            <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.availableDatesLabel)} (UTC)`}
              name={'availableDates'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DateAndTimeRangePicker
                format="DD.MM.YYYY"
                disabledDate={isDateInPast}
                placeholder={[intl.formatMessage(CommonUnitMessages.from), intl.formatMessage(CommonUnitMessages.to)]}
                inputReadOnly={true} placement={"bottomLeft"}
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                airportFromOffset={airportFromOffset}
                airportToOffset={airportToOffset}
                containerHeight="100%"
              />
            </CustomFieldInput>
          }

          {dateTypeRadioValue === EmptyLegDateTypeEnum.SPECIFIC &&
            <>
              <CustomFieldInput
                label={`${intl.formatMessage(CommonFormMessages.departureDateLabel)} (UTC)`}
                name={'departureDate'}
                rules={getDateAndTimePickerValidationRules(intl, true)}
                style={{ marginBottom: '5px' }}>
                <DateAndTimePicker
                  format="YYYY-MM-DD"
                  placeholder={intl.formatMessage(CommonFormMessages.departureDatePlaceholder)}
                  disabledDate={isDateInPast}
                  inputReadOnly={true} placement={"bottomLeft"}
                  getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                  containerHeight="100%"
                />
              </CustomFieldInput>
              {departureDate && airportFromId && (
                <LocalTimeWrapper style={{ marginBottom: '16px' }}>
                  <LocalTimeDisplay
                    utcDate={departureDate}
                    airportOffset={airportFromOffset}
                  />
                </LocalTimeWrapper>
              )}
            </>
          }
        </Col>

        <Row justify={"end"} style={{marginBottom: 8}}>
          <CalculateSwitcher
            disabled={isAutoFlyRouteDisabled}
            isAutoCalculationApplied={isAutoFlyRouteCalcApplied}
            setIsAutoCalculationApplied={setIsAutoFlyRouteApplied}
            onClick={onCalculateFlight}/>
        </Row>

        <Row gutter={8}>
          <Col span={isMobile ? 12 : 8}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.flyHoursLabel)}
              name={'hours'}
              rules={[
                {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                {min: 0, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinimumZero)},
                {max: 99, type: "number", message: intl.formatMessage(CommonFormMessages.ruleHugeNumber)},
              ]}
            >
              <CustomInputNumber placeholder={"__"} addonAfter={intl.formatMessage(CommonUnitMessages.hour)}
                                 maxLength={2}
                                 precision={0}
                                 onlyDigits/>
            </CustomFieldInput>
          </Col>

          <Col span={isMobile ? 12 : 8}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.flyMinutesLabel)}
              name={'minutes'}
              rules={[
                {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                {min: 0, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinimumZero)},
                {max: 59, type: "number", message: intl.formatMessage(CommonFormMessages.ruleHugeNumber)},
              ]}
            >
              <CustomInputNumber placeholder={"__"} addonAfter={intl.formatMessage(CommonUnitMessages.minute)}
                                 maxLength={2}
                                 precision={0}
                                 onlyDigits/>
            </CustomFieldInput>
          </Col>

          <Col span={isMobile ? 24 : 8}>
            <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.distanceLabel).replace(', km', '')}, ${getUnitLabel(userDistanceUnit)}`}
              name={'distance'}
              rules={[
                {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                {min: 0, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinimumZero)},
                {max: 99999, type: "number", message: intl.formatMessage(CommonFormMessages.ruleHugeNumber)},
              ]}
              getValueFromEvent={(value) => {
                if (value === null || value === undefined || value === '') {
                  return value;
                }
                const numValue = typeof value === 'number' ? value : Number(value);
                if (numValue > 99999) {
                  return 99999;
                }
                // Limit string representation to 5 digits
                const stringValue = numValue.toString();
                if (stringValue.length > 5) {
                  return Number(stringValue.slice(0, 5));
                }
                return numValue;
              }}
            >
              <CustomInputNumber
                placeholder={"__"}
                addonAfter={getUnitLabel(userDistanceUnit)}
                onlyDigits
                max={99999}
                parser={(value) => {
                  if (!value) return '' as any;
                  // Remove spaces and non-digits, then limit to 5 digits
                  const withoutSpaces = value.replace(/\s/g, '');
                  const digitsOnly = withoutSpaces.replace(/\D/g, '');
                  return digitsOnly.slice(0, 5) as any;
                }}
                onKeyDown={(e) => {
                  const target = e.target as HTMLInputElement;
                  const currentValue = target.value?.replace(/\s/g, '') || '';
                  // Prevent input if already 5 digits and key is not a control key
                  if (currentValue.length >= 5 && 
                      e.key.length === 1 && 
                      /[0-9]/.test(e.key) &&
                      !(e.ctrlKey || e.metaKey || e.altKey)) {
                    e.preventDefault();
                  }
                }}
                onChange={(value) => {
                  if (value !== null && value !== undefined && value > 99999) {
                    form.setFieldsValue({ distance: 99999 });
                  }
                }}
              />
            </CustomFieldInput>
          </Col>
        </Row>

        <Row style={{marginBottom: 16}}>
          <Col span={24}>
            <CustomSwitch labelFirst
              checked={isPriceOnRequest}
              onChange={(checked) => {
                setIsPriceOnRequest(checked);
                if (checked) {
                  // Clear price and currency fields when "Price on request" is enabled
                  form.setFieldsValue({
                    priceAmount: undefined,
                    priceCurrencyId: undefined,
                  });
                }
              }}
              label={intl.formatMessage(CommonFormMessages.priceOnRequestLabel)}
            />
          </Col>
        </Row>

        <Row gutter={8}>
          <Col span={12}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.priceLabel)}
              name={'priceAmount'}
              rules={[
                {required: !isPriceOnRequest, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                {
                  min: 0,
                  type: "number",
                  message: intl.formatMessage(CommonFormMessages.ruleMinimumZero)
                }
              ]}
            >
              <CustomInputNumber 
                controls={false} 
                placeholder={intl.formatMessage(CommonFormMessages.pricePlaceholder)}
                style={{width: '100%'}}
                disabled={isPriceOnRequest}
                onlyDigits
                parser={(value) => {
                  if (!value) return '' as any;
                  // Remove spaces and non-digits, then limit to 8 digits
                  const withoutSpaces = value.replace(/\s/g, '');
                  const digitsOnly = withoutSpaces.replace(/\D/g, '');
                  if (!digitsOnly) return '' as any;
                  return digitsOnly.slice(0, 8) as any;
                }}
                onKeyDown={(e) => {
                  const target = e.target as HTMLInputElement;
                  const currentValue = target.value?.replace(/\s/g, '') || '';
                  // Prevent input if already 8 digits and key is not a control key
                  if (currentValue.length >= 8 && 
                      e.key.length === 1 && 
                      /[0-9]/.test(e.key) &&
                      !(e.ctrlKey || e.metaKey || e.altKey)) {
                    e.preventDefault();
                  }
                }}
              />
            </CustomFieldInput>
          </Col>
          <Col span={12}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.currencyLabel)}
              name={'priceCurrencyId'}
              rules={[{required: !isPriceOnRequest, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DebounceSelect
                fetchOptions={fetchCurrencies}
                placeholder={intl.formatMessage(CommonFormMessages.currencyPlaceholder)}
                disabled={isPriceOnRequest}
              />
            </CustomFieldInput>
          </Col>
        </Row>

        <Form.Item>
          <CustomButton
            block
            heightSize={"l"}
            type={'primary'}
            htmlType={'submit'}
            loading={isLoading}
          >
            {intl.formatMessage(CommonButtonMessages.create)}
          </CustomButton>
        </Form.Item>
      </Form>
    </CustomModal>
  );
};
